/**
 * CC6Computing — synastry computing loader screen.
 * data-screen-label="cc-06 Computing synastry"
 *
 * - Dual-arc SVG ring with spin / spinSlow CSS keyframes
 * - 12-dot orbit
 * - Brand logo with sigilCoreBreathe animation
 * - 3 sequential progress lines
 * - On mount: polls GET /api/v1/sku/compat-check/result/by-invite every 2s (max 30 polls)
 *   (Wave D #2b — superseded /by-partner)
 * - status === 'awaiting_partner' keeps polling (partner has not yet accepted in bot DM)
 * - On completed: sends RUN_COMPLETE(output) → transitions to CC7Result
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../../lib/api-client';
import { useAuth } from '../../../store/auth';
import { CCBackdrop, CCStars } from './shared';
import type { UseCompatFlowReturn, CompatRunOutput } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

const MAX_POLLS = 30;

// --------------------------------------------------------------------------
// SVG constants
// --------------------------------------------------------------------------

const SIZE = 240;
const R = SIZE / 2 - 4;        // outer arc radius
const R_DASH = SIZE / 2 - 22;  // inner orbit radius

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC6ComputingProps {
  flow: UseCompatFlowReturn;
}

export function CC6Computing({ flow }: CC6ComputingProps): JSX.Element {
  const { t } = useTranslation('flows');
  const jwt = useAuth((s) => s.jwt);
  const { compatInviteId } = flow;

  const [attempts, setAttempts] = useState(0);

  type PollResponse = {
    status: 'pending' | 'completed' | 'failed' | 'awaiting_partner';
    check?: CompatRunOutput;
    inviteStatus?: string;
  };

  const { data, error } = useQuery<PollResponse>({
    queryKey: ['compat-result', compatInviteId],
    queryFn: async () => {
      setAttempts((prev) => prev + 1);
      const res = await apiFetch(
        `/api/v1/sku/compat-check/result/by-invite?compatInviteId=${encodeURIComponent(compatInviteId!)}`,
        jwt ?? '',
        { method: 'GET' },
      );
      if (!res.ok) throw new Error(`poll_failed_${res.status}`);
      return res.json() as Promise<PollResponse>;
    },
    enabled: !!jwt && !!compatInviteId && attempts < MAX_POLLS,
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.status === 'completed' || d?.status === 'failed') return false;
      // 'pending' and 'awaiting_partner' both keep polling
      return 2000;
    },
    retry: 2,
  });

  useEffect(() => {
    if (data?.status === 'completed' && data.check) {
      flow.send({ type: 'RUN_COMPLETE', output: data.check });
    } else if (data?.status === 'failed') {
      flow.send({ type: 'ERROR', message: t('compat.computing.errorFailed') });
    } else if (attempts >= MAX_POLLS && data?.status !== 'completed') {
      flow.send({ type: 'ERROR', message: t('compat.computing.errorTimeout') });
    } else if (error) {
      flow.send({ type: 'ERROR', message: t('compat.computing.errorConnection') });
    }
  }, [data, error, attempts, flow]);

  // Generate 12 orbit dots
  const dots = Array.from({ length: 12 }).map((_, i) => {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    return {
      cx: SIZE / 2 + Math.cos(angle) * R_DASH,
      cy: SIZE / 2 + Math.sin(angle) * R_DASH,
      r: i === 0 ? 2.6 : 1.5,
      opacity: i === 0 ? 1 : 0.55,
      glow: i === 0,
    };
  });

  return (
    <div className={styles.root} data-screen-label="cc-06 Computing synastry">
      <CCBackdrop tone="cosmos" />
      <CCStars seed={6} count={100} />

      <div className={styles.content}>
        <div className={styles.computingCenter}>
          {/* Ring animation */}
          <div className={styles.computingRingWrap} style={{ width: SIZE, height: SIZE }}>
            {/* Outer spinning arcs */}
            <svg
              className={styles.spinRing}
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="ccArcGold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#f0c850" />
                  <stop offset="100%" stopColor="#e89a3c" />
                </linearGradient>
                <linearGradient id="ccArcViolet" x1="1" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#c89cff" />
                  <stop offset="100%" stopColor="#7a3dff" />
                </linearGradient>
              </defs>
              {/* Faint full ring */}
              <circle
                cx={SIZE / 2} cy={SIZE / 2} r={R}
                fill="none"
                stroke="#d4af37"
                strokeOpacity="0.18"
                strokeWidth="0.6"
              />
              {/* Gold arc — top half */}
              <circle
                cx={SIZE / 2} cy={SIZE / 2} r={R}
                fill="none"
                stroke="url(#ccArcGold)"
                strokeWidth="2"
                strokeDasharray={`${Math.PI * R * 0.32} ${Math.PI * 2 * R}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                style={{ filter: 'drop-shadow(0 0 10px #f0c850)' }}
              />
              {/* Violet arc — bottom half */}
              <circle
                cx={SIZE / 2} cy={SIZE / 2} r={R}
                fill="none"
                stroke="url(#ccArcViolet)"
                strokeWidth="2"
                strokeDasharray={`${Math.PI * R * 0.32} ${Math.PI * 2 * R}`}
                strokeLinecap="round"
                transform={`rotate(90 ${SIZE / 2} ${SIZE / 2})`}
                style={{ filter: 'drop-shadow(0 0 10px #c89cff)' }}
              />
            </svg>

            {/* Inner counter-rotating dashed orbit + 12 dots */}
            <svg
              className={styles.spinSlowRing}
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              aria-hidden="true"
            >
              <circle
                cx={SIZE / 2} cy={SIZE / 2} r={R_DASH}
                fill="none"
                stroke="#d4af37"
                strokeOpacity="0.4"
                strokeWidth="0.5"
                strokeDasharray="3 6"
              />
              {dots.map((dot, i) => (
                <circle
                  key={i}
                  cx={dot.cx}
                  cy={dot.cy}
                  r={dot.r}
                  fill="#f0c850"
                  opacity={dot.opacity}
                  style={dot.glow ? { filter: 'drop-shadow(0 0 5px #f0c850)' } : undefined}
                />
              ))}
            </svg>

            {/* Glow behind logo */}
            <div
              className={styles.computingLogoGlow}
              style={{ width: 120, height: 120, marginTop: -60, marginLeft: -60 }}
              aria-hidden="true"
            />

            {/* Brand logo */}
            <img
              src="/assets/logo-transparent.png"
              alt=""
              className={styles.computingLogo}
              style={{ width: 110, height: 110, marginTop: -55, marginLeft: -55 }}
              aria-hidden="true"
            />
          </div>

          {/* Heading */}
          <h2 className={styles.computingHeading}>
            {t('compat.computing.headingPrefix')}<br />
            <em className={styles.gradAccentCool}>{t('compat.computing.headingAccent')}</em>
          </h2>

          {/* Progress lines */}
          <div className={styles.progressLines} aria-live="polite" aria-label={t('compat.computing.progressAria')}>
            <span className={styles.progressDone}>
              <span className={styles.progressDoneIcon}>✓</span> {t('compat.computing.step1')}
            </span>
            <span className={styles.progressDone}>
              <span className={styles.progressDoneIcon}>✓</span> {t('compat.computing.step2')}
            </span>
            <span className={styles.progressActive}>
              <span className={styles.progressActiveIcon}>↻</span> {t('compat.computing.step3')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
